import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || '';

function Check() {
  const [searchParams] = useSearchParams();
  const domainParam = searchParams.get('domain');
  const nameParam = searchParams.get('name');
  const queryParam = nameParam || domainParam;

  const [domain, setDomain] = useState(queryParam || '');
  const [status, setStatus] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (queryParam) {
      checkDomain(queryParam);
    }
  }, [queryParam]);

  const checkDomain = async (d) => {
    setLoading(true);
    setError('');
    setAgentData(null);

    try {
      // First check status in registry
      const statusResponse = await fetch(`${API_URL}/v1/status/${d}`);
      const statusData = await statusResponse.json();
      setStatus(statusData);

      // If verified, fetch agent data from their server
      if (statusData.verified && statusData.agentUrl) {
        try {
          const agentResponse = await fetch(statusData.agentUrl);
          if (agentResponse.ok) {
            const data = await agentResponse.json();
            setAgentData(data);
          }
        } catch (err) {
          console.warn('Could not fetch agent data from origin:', err);
        }
      }
    } catch (err) {
      setError('Failed to check domain status');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (domain.trim()) {
      checkDomain(domain.trim());
    }
  };

  const getTrustTier = () => {
    if (!status?.verified) return null;

    // Simple trust tier based on verification method
    const tiers = {
      'clawshield-standard': { name: 'Standard', color: 'coral', level: 2 },
      'spring-ai-standard': { name: 'Standard', color: 'coral', level: 2 },
      'dns-txt': { name: 'Basic', color: 'blue', level: 1 }
    };

    return tiers[status.verificationMethod] || { name: 'Verified', color: 'gray', level: 1 };
  };

  const trustTier = getTrustTier();

  return (
    <div className="py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Search Form */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Check Agent</h1>
          <p className="text-gray-600 mb-6">
            Search by agent name or domain
          </p>
          <form onSubmit={handleSubmit} className="flex max-w-md mx-auto">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Agent name or domain..."
              className="input rounded-r-none"
            />
            <button type="submit" className="btn-primary rounded-l-none whitespace-nowrap">
              Check
            </button>
          </form>
        </div>

        {/* Loading */}
        {loading && (
          <div className="card p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Checking domain...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Results */}
        {!loading && status && (
          <div className="card overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-8 ${status.verified ? 'bg-primary-500' : 'bg-gray-400'} text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-1">{status.agentName || status.domain}</h2>
                  {status.agentName && (
                    <p className="text-sm opacity-80 mb-2">{status.domain}</p>
                  )}
                  <div className="flex items-center space-x-2">
                    {status.verified ? (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Verified Agent</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Not Verified</span>
                      </>
                    )}
                  </div>
                </div>
                {status.verified && (
                  <img
                    src={`${API_URL}/v1/badge/${status.domain}`}
                    alt="Badge"
                    className="h-7"
                  />
                )}
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {status.verified ? (
                <>
                  {/* Agent Info */}
                  {agentData && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h3>
                      <dl className="space-y-3">
                        {agentData.name && (
                          <div>
                            <dt className="text-sm text-gray-500">Name</dt>
                            <dd className="text-gray-900 font-medium">{agentData.name}</dd>
                          </div>
                        )}
                        {agentData.description && (
                          <div>
                            <dt className="text-sm text-gray-500">Description</dt>
                            <dd className="text-gray-900">{agentData.description}</dd>
                          </div>
                        )}
                        {agentData.version && (
                          <div>
                            <dt className="text-sm text-gray-500">Version</dt>
                            <dd className="text-gray-900">{agentData.version}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* Trust Tier */}
                  {trustTier && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Trust Tier</h3>
                      <div className="flex items-center space-x-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          trustTier.color === 'coral' ? 'bg-primary-100 text-primary-800' :
                          trustTier.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {trustTier.name}
                        </div>
                        <div className="flex space-x-1">
                          {[1, 2, 3].map((level) => (
                            <div
                              key={level}
                              className={`w-4 h-4 rounded ${
                                level <= trustTier.level ? 'bg-primary-500' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Verification Details */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Verification Details</h3>
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-gray-500">Method</dt>
                        <dd className="text-gray-900 capitalize">
                          {status.verificationMethod?.replace(/-/g, ' ')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Verified At</dt>
                        <dd className="text-gray-900">
                          {new Date(status.verifiedAt).toLocaleDateString()}
                        </dd>
                      </div>
                      {status.agentUrl && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Agent URL</dt>
                          <dd className="text-gray-900 break-all">
                            <a
                              href={status.agentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline"
                            >
                              {status.agentUrl}
                            </a>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Warning about fetching keys */}
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">Zero-Knowledge Architecture</p>
                        <p className="mt-1">
                          ClawShield does not store public keys. To verify signatures, fetch the
                          agent's public key directly from{' '}
                          <a
                            href={status.agentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            their origin server
                          </a>.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4">
                    This domain is not verified in the ClawShield registry.
                  </p>
                  <Link to="/verify" className="btn-primary inline-block">
                    Verify This Agent
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !status && !error && !domainParam && (
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-gray-600">
              Enter a domain above to check its verification status.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Check;
